/**
 * English (default). Keys are shared by the bot and the Mini App. Plural keys use the suffixes
 * returned by Intl.PluralRules (`.one`, `.few`, `.many`, `.other`); `{name}` placeholders are interpolated.
 */
export const en: Record<string, string> = {
  'app.title': 'Spin the Bottle',

  'common.ok': 'OK',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.retry': 'Retry',
  'common.save': 'Save',
  'common.send': 'Send',
  'common.loading': 'Loading…',
  'common.reconnecting': 'Connection lost. Reconnecting…',
  'common.offline': 'You are offline',
  'common.hearts': '❤ {count}',
  'common.optional': 'optional',

  'errors.BAD_REQUEST': 'Something is wrong with this request.',
  'errors.VALIDATION_FAILED': 'Please check what you entered.',
  'errors.UNAUTHORIZED': 'Please reopen the game from Telegram.',
  'errors.TOKEN_EXPIRED': 'Your session expired. Reconnecting…',
  'errors.FORBIDDEN': 'You can’t do that.',
  'errors.NOT_FOUND': 'Not found.',
  'errors.CONFLICT': 'This was changed by someone else. Try again.',
  'errors.UNPROCESSABLE': 'This action is not possible right now.',
  'errors.RATE_LIMITED': 'Too fast! Wait a moment.',
  'errors.PAYLOAD_TOO_LARGE': 'That’s too long.',
  'errors.UNIQUE_VIOLATION': 'This already exists.',
  'errors.INTERNAL_ERROR': 'Something went wrong. Please try again.',
  'errors.SERVICE_UNAVAILABLE': 'The game is restarting. Back in a moment.',
  'errors.INIT_DATA_INVALID': 'Please open the game from the Telegram bot.',
  'errors.INIT_DATA_EXPIRED': 'This launch link is too old. Reopen the game from the bot.',
  'errors.BANNED': 'Your account is blocked.',
  'errors.ONBOARDING_REQUIRED': 'Please finish your profile first.',
  'errors.SESSION_REPLACED': 'The game was opened on another device.',
  'errors.LOGIN_FAILED': 'Wrong login or password.',
  'errors.WRONG_PASSWORD': 'The current password is wrong.',
  'errors.ROOM_NOT_FOUND': 'This table no longer exists.',
  'errors.ROOM_FULL': 'This table is full.',
  'errors.ROOM_BUSY': 'The table is busy. Try again.',
  'errors.NOT_IN_ROOM': 'You are not at a table.',
  'errors.NOT_YOUR_TURN': 'It’s not your turn.',
  'errors.NOT_IN_ROUND': 'You are not part of this round.',
  'errors.STALE_TURN': 'Too late for that.',
  'errors.ALREADY_DECIDED': 'You already decided.',
  'errors.MUTED': 'You can’t chat right now.',
  'errors.BLOCKED': 'You can’t interact with this player.',
  'errors.MESSAGE_REJECTED': 'This message can’t be sent. Links and offensive words are not allowed.',
  'errors.CANNOT_TARGET_SELF': 'You can’t do that to yourself.',
  'errors.INSUFFICIENT_FUNDS': 'Not enough hearts.',
  'errors.INSUFFICIENT_GM': 'Not enough GM.',
  'errors.TRANSFER_LIMIT': 'You have sent enough for today. Try again tomorrow.',
  'errors.NOT_FRIENDS': 'You can send only to your friends.',
  'errors.ALREADY_CLAIMED': 'Already claimed today. Come back tomorrow!',
  'errors.GIFT_NOT_FOUND': 'This gift is not available.',
  'errors.VIDEO_UNAVAILABLE': 'This video can’t be played in the game. Try another link.',
  'errors.NO_CHANCES': 'You have no chances left.',
  'errors.NO_BOOSTER': 'You don’t have this booster.',
  'errors.PRODUCT_NOT_FOUND': 'This pack is not available.',

  'gender.MALE': 'Man',
  'gender.FEMALE': 'Woman',
  'locale.en': 'English',
  'locale.ru': 'Русский',
  'locale.uz': 'Oʻzbekcha',
  'locale.az': 'Azərbaycanca',
  'locale.tr': 'Türkçe',

  'onboarding.title': 'Welcome to Spin the Bottle!',
  'onboarding.subtitle': 'Spin the bottle, kiss, send gifts and meet new people.',
  'onboarding.language': 'Language',
  'onboarding.name': 'Name at the table',
  'onboarding.gender': 'I am a',
  'onboarding.birth_year': 'Birth year',
  'onboarding.birth_year_hint': 'Shown as your age next to your name',
  'onboarding.rules_title': 'Table rules',
  'onboarding.rule_respect': 'Be kind. Insults, spam and links are not allowed.',
  'onboarding.rule_virtual': 'Kisses and gifts are virtual and just for fun.',
  'onboarding.rule_block': 'You can block or report anyone at any time.',
  'onboarding.adult': 'I am 18 or older and I accept the table rules',
  'onboarding.start': 'Let’s play',
  'onboarding.underage': 'Sorry, the game is only for players aged 18 and over.',

  'lobby.play': 'PLAY',
  'lobby.balance': 'Your hearts',
  'lobby.ratings': 'Ratings',
  'lobby.achievements': 'Achievements',
  'lobby.shop': 'Buy hearts',
  'lobby.settings': 'Settings',
  'lobby.invite': 'Invite friends',
  'lobby.profile': 'Profile',

  'table.title': 'Table {code}',
  'table.waiting_players': 'Waiting for more players…',
  'table.waiting_turn': 'Waiting for the next turn',
  'table.your_turn': 'Spin the bottle!',
  'table.tap_bottle': 'Tap the bottle',
  'table.spins': 'Spins the bottle',
  'table.your_choice': 'Your choice',
  'table.kiss_back': 'Will kiss back?',
  'table.will_they': 'Will they kiss or not?',
  'table.kiss': 'Kiss',
  'table.refuse': 'Refuse',
  'table.result_mutual': 'Mutual kiss! 💋',
  'table.result_one': '{from} kissed {to} 💋',
  'table.result_none': 'No kiss this time',
  'table.change': 'Change table',
  'table.leave': 'Leave table',
  'table.chat_placeholder': 'Write a message here',
  'table.chat_empty': 'Say hi to everyone 👋',
  'table.music_on': 'Music on',
  'table.music_off': 'Music off',
  'table.now_playing': 'Now playing',
  'table.tap_to_listen': 'Tap to listen',
  'table.music_error': 'This video can’t be played here',
  'table.dj_title': 'Order a song for the table',
  'table.dj_hint': 'Paste a YouTube link. Everyone at the table will hear it.',
  'table.dj_order': 'Order for ❤ {price}',
  'table.dj_queue': 'In queue: {count}',
  'table.idle_kicked': 'You were removed from the table for inactivity.',
  'table.session_replaced': 'The game was opened on another device.',
  'table.kisses.one': '{count} kiss',
  'table.kisses.other': '{count} kisses',
  'table.joined': '{name} joined the table',
  'table.left': '{name} left the table',
  'table.gift_sent': '{from} sent {emoji} to {to}',
  'table.dj_dedicated': '{name} dedicated a song to {to}: {title}',
  'table.dj_for': 'for {name}',
  'table.dj_dedicate': 'Dedicate a song to {name}',
  'table.dj_video': 'Show as a music video (❤ {price})',
  'gifts.category.romance': 'Romance',
  'gifts.category.hats': 'Hats & crowns',
  'gifts.category.animals': 'Animals',
  'gifts.category.music': 'Music',
  'gifts.category.vip': 'VIP',
  'table.dj_ordered': '{name} ordered a song: {title}',

  'player.send_gift': 'Send a gift',
  'player.hidden_message': 'Hidden message',
  'player.dare': 'Dare',
  'player.admire': 'Admire for ❤ {price}',
  'player.admirer': 'Admirer: {name} (❤ {price})',
  'player.no_admirer': 'No admirer yet',
  'player.block': 'Block',
  'player.unblock': 'Unblock',
  'player.report': 'Report',
  'player.you': 'This is you',
  'player.achievements.one': '{count} achievement',
  'player.achievements.other': '{count} achievements',

  'gifts.title': 'Send a gift to {name}',
  'gifts.category.flowers': 'Flowers',
  'gifts.category.drinks': 'Drinks',
  'gifts.category.sweets': 'Sweets',
  'gifts.category.fun': 'Fun',
  'gifts.category.status': 'Luxury',
  'gifts.category.pranks': 'Pranks',
  'gifts.category.seasonal': 'Seasonal',

  'ratings.title': 'Ratings',
  'ratings.kisses': 'Most kissed',
  'ratings.dj': 'Best DJs',
  'ratings.day': 'Today',
  'ratings.week': 'This week',
  'ratings.month': 'This month',
  'ratings.all': 'All time',
  'ratings.you': 'You',
  'ratings.empty': 'No one here yet. Be the first!',

  'achievements.title': 'Achievements',
  'achievements.progress': '{progress} / {threshold}',
  'achievements.reward': 'Reward: ❤ {reward}',

  'errors.VIP_REQUIRED': 'This is for VIP players.',
  'errors.NOT_OWNED': 'You don’t have this item yet.',
  'lobby.vip': 'VIP',
  'lobby.styling': 'Styling',
  'vip.title': 'VIP status',
  'vip.active_until': 'VIP until {date}',
  'vip.inactive': 'Stand out at every table.',
  'vip.perk_name': 'Golden name and a VIP badge at the table and in chat',
  'vip.perk_bottle': 'The VIP bottle for your table',
  'vip.perk_frames': 'Exclusive VIP frames',
  'vip.perk_gifts': 'VIP gifts and stickers',
  'vip.plan': '{days} days',
  'vip.buy': 'Get for ❤ {price}',
  'vip.extend': 'Extend for ❤ {price}',
  'vip.bought': 'You are VIP until {date}! 👑',
  'styling.title': 'Styling',
  'styling.none': 'No styling',
  'styling.none_hint': 'Leave the profile pic and user name without styling',
  'styling.buy': 'Buy for ❤ {price}',
  'styling.equip': 'Wear',
  'styling.equipped': 'Wearing',
  'styling.remove': 'Take off',
  'styling.owned': 'Yours',
  'styling.vip_only': 'VIP only',
  'styling.reward.league_champion': 'Reward: win a league day',
  'styling.reward.kiss_master': 'Reward: receive 1000 kisses',
  'bottle.put': 'Put it on the table for ❤ {price}',
  'bottle.pick': 'Pick a bottle',
  'bottle.title': 'Change the bottle',
  'bottle.hint': 'The bottle changes for everyone at this table.',
  'bottle.current': 'On the table',
  'bottle.changed': '{name} put the “{skin}” bottle on the table 🍾',
  'table.change_bottle': 'Change the bottle',
  'bottle.classic': 'Classic',
  'bottle.emerald': 'Emerald',
  'bottle.amber': 'Amber',
  'bottle.champagne': 'Champagne',
  'bottle.cola': 'Cola',
  'bottle.lemonade': 'Lemonade',
  'bottle.whisky': 'Whisky',
  'bottle.wine': 'Red wine',
  'bottle.sparkle': 'Sparkle',
  'bottle.ice': 'Ice',
  'bottle.gold': 'Gold',
  'bottle.potion': 'Love potion',
  'bottle.baby': 'Baby bottle',
  'bottle.vip': 'VIP',
  'frame.rose_garden': 'Rose garden',
  'frame.neon_pink': 'Neon pink',
  'frame.neon_cyan': 'Neon cyan',
  'frame.hearts': 'Hearts',
  'frame.candy': 'Candy',
  'frame.bow': 'Bow',
  'frame.leaves': 'Autumn',
  'frame.flame': 'Flame',
  'frame.ice': 'Ice',
  'frame.butterfly': 'Butterfly',
  'frame.emerald': 'Emerald',
  'frame.ruby': 'Ruby',
  'frame.sapphire': 'Sapphire',
  'frame.devil': 'Little devil',
  'frame.angel': 'Angel',
  'frame.galaxy': 'Galaxy',
  'frame.rainbow': 'Rainbow',
  'frame.royal': 'Royal',
  'frame.vip_gold': 'VIP gold',
  'frame.vip_diamond': 'VIP diamond',
  'frame.league_champion': 'League champion',
  'frame.kiss_master': 'Kiss master',
  'stickers.title': 'My stickers',
  'stickers.pack.basic': 'Basic',
  'stickers.pack.love': 'Love',
  'stickers.pack.party': 'Party',
  'stickers.pack.animals': 'Animals',
  'stickers.pack.vip': 'VIP',
  'stickers.cost': 'Each sticker costs ❤ {price} and floats over your avatar.',
  'stickers.buy': 'Unlock for ❤ {price}',
  'stickers.vip_only': 'For VIP players',
  'ratings.emotional': 'Most emotional',
  'wheel.title': 'Wheel of Fortune',
  'wheel.spin': 'Spin',
  'wheel.chances': 'Chances: {count}',
  'wheel.free': 'Free chance of the day',
  'wheel.free_taken': 'Come back tomorrow for a free chance',
  'wheel.shop': 'Shop of Chances',
  'wheel.pack': '{count} chances',
  'wheel.pack_one': '1 chance',
  'wheel.help': 'How it works',
  'wheel.help_text':
    'Each spin costs one chance. Prizes are drawn by the server with the odds below; chances you win can be spent again. Boosters are used from the league screen.',
  'wheel.odds': 'Odds',
  'wheel.won': 'You won: {prize}',
  'wheel.jackpot_banner': '🎰 {name} hit the jackpot: {prize}!',
  'wheel.big_win_banner': '🎡 {name} won {prize} on the wheel',
  'wheel.need_chance': 'No chances left: get a free one tomorrow or buy some below.',
  'wheel.boosters': 'My boosters',
  'wheel.prize.hearts_3': '❤ 3',
  'wheel.prize.hearts_5': '❤ 5',
  'wheel.prize.hearts_25': '❤ 25',
  'wheel.prize.hearts_100': '❤ 100',
  'wheel.prize.jackpot': 'Jackpot ❤ 1000',
  'wheel.prize.chance_1': '+1 chance',
  'wheel.prize.chance_3': '+3 chances',
  'wheel.prize.boost_x2': 'Booster ×2',
  'wheel.prize.boost_limit': 'Booster +10 limit',
  'wheel.prize.boost_bonus': 'Booster +5 points',
  'booster.x2': 'Double league points',
  'booster.x2_desc': 'Kisses you receive count double for 5 minutes (kiss gifts excluded).',
  'booster.limit': 'Raise the league limit',
  'booster.limit_desc': 'Today’s cap on points from received kisses goes up by 10.',
  'booster.bonus': 'Bonus league points',
  'booster.bonus_desc': '+5 league points right away, beyond any cap.',
  'lobby.wheel': 'Wheel',
  'league.title': 'Leagues',
  'league.tier.wooden': 'Wooden league',
  'league.tier.bronze': 'Bronze league',
  'league.tier.silver': 'Silver league',
  'league.tier.gold': 'Gold league',
  'league.tier.platinum': 'Platinum league',
  'league.tier.diamond': 'Diamond league',
  'league.ends_in': 'Final results in {h} h {m} min',
  'league.points': '★ {points}',
  'league.kiss_limit': 'Kiss points today: {used} / {cap}',
  'league.zone_up': 'Go up to the next league',
  'league.zone_down': 'Drop to the previous league',
  'league.rewards': 'Rewards',
  'league.chest.red': 'Red chest',
  'league.chest.blue': 'Blue chest',
  'league.chest.wooden': 'Wooden chest',
  'league.chest.box': 'Gift box',
  'league.chest_contents': '❤ {hearts} + 🎲 {chances}',
  'league.box_contents': '🎲 {chances} for {points}+ points',
  'league.place': 'Place {place}',
  'league.empty':
    'Earn your first points today to join a group: send gifts, order songs, admire players or get kissed.',
  'league.rules': 'Rules',
  'league.rules_text':
    'Leagues are daily competitions. You get a point for every heart spent on gifts, music and admiration, and for every kiss you receive (up to {cap} a day). At the end of the day the top {promote} of each group move up a league and the last {demote} drop one (in groups of {min}+ players). The top 3 get chests, everyone with {box}+ points gets a gift box.',
  'league.use': 'Use',
  'league.x2_active': '×2 is on: {time}',
  'league.used.x2': 'Received kisses count double for 5 minutes!',
  'league.used.limit': 'Your kiss limit for today grew by 10.',
  'league.used.bonus': '+5 league points!',
  'league.last_result': 'Last result: place {place} in the {tier}',
  'league.moved_up': '⬆ Promoted',
  'league.moved_down': '⬇ Relegated',
  'league.stayed': 'Stayed',
  'league.result_toast': 'League results: place {place}. {chest}',
  'league.get_boosters': 'Win boosters on the Wheel of Fortune',
  'lobby.league': 'League',
  'achievement.kisser.title': 'Kisser',
  'achievement.kisser.desc': 'Kisses given: {count}',
  'achievement.popular.title': 'Popular',
  'achievement.popular.desc': 'Kisses received: {count}',
  'achievement.mutual.title': 'Sweethearts',
  'achievement.mutual.desc': 'Mutual kisses: {count}',
  'achievement.spinner.title': 'Bottle master',
  'achievement.spinner.desc': 'Bottle spins: {count}',
  'achievement.regular.title': 'Regular',
  'achievement.regular.desc': 'Rounds played: {count}',
  'achievement.generous.title': 'Generous',
  'achievement.generous.desc': 'Gifts sent: {count}',
  'achievement.adored.title': 'Adored',
  'achievement.adored.desc': 'Gifts received: {count}',
  'achievement.dj.title': 'DJ',
  'achievement.dj.desc': 'Songs ordered: {count}',
  'achievement.chatty.title': 'Chatterbox',
  'achievement.chatty.desc': 'Chat messages: {count}',
  'achievement.daredevil.title': 'Daredevil',
  'achievement.daredevil.desc': 'Dares accepted: {count}',
  'achievement.streak.title': 'Faithful',
  'achievement.streak.desc': 'Daily bonus days in a row: {count}',
  'achievement.emotional.title': 'Emotional',
  'achievement.emotional.desc': 'Stickers sent: {count}',
  'achievement.lucky.title': 'Lucky',
  'achievement.lucky.desc': 'Wheel spins: {count}',
  'achievement.admirer.title': 'Admirer',
  'achievement.admirer.desc': 'Players admired: {count}',
  'achievement.charmer.title': 'Charmer',
  'achievement.charmer.desc': 'Times admired: {count}',
  'achievement.champion.title': 'Champion',
  'achievement.champion.desc': 'League chests won: {count}',
  'achievement.league.title': 'Climber',
  'achievement.league.desc': 'League reached: {count} of 5',
  'achievement.bartender.title': 'Bartender',
  'achievement.bartender.desc': 'Table bottles changed: {count}',
  'achievement.secret.title': 'Secret admirer',
  'achievement.secret.desc': 'Hidden messages sent: {count}',
  'ratings.expensive': 'Most expensive',
  'ratings.important': 'Most important',
  'ratings.top': 'TOP',
  'ratings.help.kisses': 'Kisses received, kiss gifts included.',
  'ratings.help.dj': 'Hearts spent on music, plus one for every gift you receive while your song plays.',
  'ratings.help.expensive': 'Your last admiration price, or the price at which you turned an admirer down.',
  'ratings.help.important':
    'Hearts spent admiring others; taken back when you are turned down or someone outbids you.',
  'ratings.help.emotional': 'Hearts spent on stickers (gestures).',
  'achievements.stars': '★ {stars} / {total}',
  'achievements.filter.all': 'All',
  'achievements.filter.done': 'Completed',
  'achievements.filter.locked': 'Locked',
  'achievements.next': 'Next star: {progress} / {goal} · ❤ {reward}',
  'achievements.complete': 'All stars earned!',
  'player.turn_down': 'Turn the admirer down',
  'player.turned_down': 'You turned {name} down',
  'notify.turned_down': '{name} turned you down',
  'activity.title': 'Bonus & Rating',
  'activity.today': 'Today',
  'activity.yesterday': 'Yesterday',
  'activity.time': 'Activity time',
  'activity.time_value': '{h} h {m} min',
  'activity.milestone': '{hours} h → ❤ {reward}',
  'activity.points': 'Activity points',
  'activity.how': 'Points rules',
  'activity.kind.message': 'Chat message',
  'activity.kind.song': 'Ordered song',
  'activity.kind.booster': 'Booster used',
  'activity.kind.gift': 'Gift sent',
  'activity.kind.round': 'Round played',
  'activity.kind.sticker': 'Sticker sent',
  'activity.kind.wheel': 'Wheel spin',
  'activity.rules':
    'Play time counts while you are active at a table (an action at least every {gap} minutes). Up to {cap} chat messages a day earn points. The daily top closes at midnight; the first {places} places get hearts. Spam does not count.',
  'activity.top': 'Daily top {size}',
  'activity.prize': '❤ {hearts}',
  'activity.ends_in': 'Closes in {h} h {m} min',
  'activity.empty': 'No activity yet today.',
  'activity.reward_toast': 'Activity reward: ❤ {hearts}',
  'activity.top_toast': 'Daily activity top: place {place}, ❤ {hearts}',
  'lobby.bonus': 'Bonus',
  'friends.title': 'Friends list',
  'friends.add': 'Add friend',
  'friends.requested': 'Request sent',
  'friends.accept': 'Accept',
  'friends.decline': 'Decline',
  'friends.remove': 'Remove from friends',
  'friends.friends': 'Friends',
  'friends.incoming': 'Friend requests',
  'friends.outgoing': 'Sent requests',
  'friends.empty': 'No friends yet.',
  'friends.online': 'online',
  'friends.at_table': 'at a table',
  'friends.join': 'Join',
  'friends.count': 'Friends: {count}',
  'friends.hidden': 'Friends are hidden',
  'friends.privacy': 'Who sees my friends',
  'friends.privacy.everyone': 'Everyone',
  'friends.privacy.me': 'Only me',
  'notify.friend_request': '{name} wants to be your friend',
  'notify.friend_accepted': '{name} accepted your friend request',
  'lobby.friends': 'Friends',
  'settings.gender': 'Gender',
  'settings.gender_hint': 'You can change it once every {days} days, not while sitting at a table.',
  'settings.gender_next': 'Next change: {date}',
  'errors.GENDER_COOLDOWN': 'You changed your gender recently. Try again later.',
  'errors.TOO_MANY_FRIENDS': 'Your friend list is full.',
  'errors.TRANSLATION_UNAVAILABLE': 'Translation is not available right now.',
  'table.show_translation': 'Show translation',
  'table.hide_translation': 'Hide translation',
  'table.translating': 'Translating…',
  'player.league': '{league}',
  'player.send_gift_title': 'Send a gift to {name}',
  'settings.photo_change': 'Change photo',
  'settings.photo_reset': 'Use my Telegram photo',
  'settings.photo_hint':
    'Your Telegram photo is used until you upload your own. Inappropriate photos are removed and can lead to a ban.',
  'settings.photo_preview': 'New photo',
  'settings.photo_saved': 'Photo updated',
  'settings.name_reset': 'Use my Telegram name',
  'errors.UNSUPPORTED_IMAGE': 'This picture can’t be used. Try another photo.',
  'lobby.domino': 'Domino',
  'domino.title': 'Domino',
  'domino.rules': 'Classic dominoes with a boneyard · first to {target} points takes the pot',
  'domino.rake': 'House share {percent}%',
  'domino.quick': 'Quick game',
  'domino.free': 'Free',
  'domino.create': 'Open a room',
  'domino.seats': 'Players',
  'domino.stake_label': 'Stake',
  'domino.open_rooms': 'Rooms waiting for players',
  'domino.no_rooms': 'No open rooms yet: open one or start a quick game.',
  'domino.join': 'Join',
  'domino.back_to_game': 'You are in a room: back to the game',
  'domino.waiting': 'Waiting for players… {n}/{seats}',
  'domino.leave': 'Leave',
  'domino.leave_confirm': 'Leave the game? You lose and your stake stays in the pot.',
  'domino.leave_refund': 'Leave the room (your stake comes back)',
  'domino.pot': 'Pot ❤ {pot}',
  'domino.round': 'Round {round} · to {target}',
  'domino.your_turn': 'Your turn',
  'domino.turn_of': '{name} is moving',
  'domino.draw': 'Draw ({n})',
  'domino.pass': 'Pass',
  'domino.choose_end': 'Which end?',
  'domino.left': '◀ Left',
  'domino.right': 'Right ▶',
  'domino.boneyard': 'Boneyard {n}',
  'domino.drew': '{name} drew {n}',
  'domino.passed': '{name} passed',
  'domino.auto': '{name} ran out of time',
  'domino.round_won': '{name} wins the round: +{points}',
  'domino.round_blocked': 'Blocked! {name} has the lowest hand: +{points}',
  'domino.round_tie': 'Blocked with a tie: no points this round',
  'domino.next_round': 'Next round in {s} s',
  'domino.game_won': '{name} wins the game!',
  'domino.you_won': 'You won ❤ {payout}!',
  'domino.out': 'out',
  'domino.to_lobby': 'Back to rooms',
  'domino.cancelled': 'The room was closed; stakes were returned.',
  'domino.started': 'Your domino game has started!',
  'domino.points': '{score} pts',
  'domino.empty_seat': 'Free seat',
  'domino.first_move': 'Open with the highlighted double',
  'errors.ILLEGAL_MOVE': 'That tile does not fit there.',
  'errors.DOMINO_DISABLED': 'Domino is not available.',
  'errors.ALREADY_IN_GAME': 'You are already in a domino room.',
  'lobby.live': 'Live',
  'live.title': 'Live',
  'live.go_live': 'Go live',
  'live.vip_only': 'VIP players go live; anyone can watch.',
  'live.connect_failed':
    'Could not connect to the stream video. Check your internet (or try mobile data) and open it again.',
  'live.no_webrtc':
    'This Telegram app cannot play live video. Open the game in Telegram on your phone or at web.telegram.org.',
  'live.empty': 'Nobody is live right now.',
  'live.viewers': '{n} watching',
  'live.start_title': 'Your stream',
  'live.title_placeholder': 'What is your stream about?',
  'live.rules':
    'I am 18+. I will not show nudity or minors and will not share contacts. Breaking the rules ends the stream and can ban the account.',
  'live.start': 'Start streaming',
  'live.camera_needed': 'Allow the camera and microphone to go live',
  'live.end': 'End stream',
  'live.end_confirm': 'End your stream now?',
  'live.ended': 'The stream has ended',
  'live.ended_reports': 'The stream was ended after reports',
  'live.ended_admin': 'An admin ended the stream',
  'live.ended_lost': 'The streamer lost the connection',
  'live.back': 'Back to streams',
  'live.chat_placeholder': 'Say something nice…',
  'live.sent_gift': '{name} sent {emoji}',
  'live.report': 'Report',
  'live.reported': 'Thanks, the admins will take a look.',
  'live.tap_sound': '🔊 Tap for sound',
  'live.connecting': 'Connecting…',
  'live.gift_title': 'A gift for {name}',
  'live.you_are_live': 'You are live',
  'live.waiting_video': 'Waiting for the video…',
  'live.like': 'Like',
  'live.gift': 'Gift',
  'live.camera': 'Camera',
  'live.admin_tab': 'Live',
  'live.admin_end': 'End',
  'live.admin_reports': '{n} reports',
  'live.admin_none': 'No streams yet.',
  'errors.LIVE_DISABLED': 'Live streams are not available.',
  'errors.ALREADY_LIVE': 'You are already live.',
  'errors.STREAM_ENDED': 'The stream is over.',

  'settings.title': 'Settings',
  'settings.sound': 'Sounds',
  'settings.music': 'Music',
  'settings.notifications': 'Notify me about hidden messages in the bot',
  'settings.language': 'Language',
  'settings.name': 'Name at the table',
  'settings.birth_year': 'Birth year',
  'settings.blocked': 'Blocked players',
  'settings.support': 'Support',
  'settings.saved': 'Saved',

  'invite.title': 'Invite friends',
  'invite.text': 'You and your friend get ❤ {bonus} when they play their first round.',
  'invite.share': 'Share invite link',
  'invite.copy': 'Copy link',
  'invite.copied': 'Link copied',
  'invite.stats': 'Invited: {invited} · Rewarded: {rewarded}',
  'invite.share_text': 'Join me in Spin the Bottle! 🍾💋',

  'notify.admired': '{name} started admiring you for ❤ {price}',
  'notify.outbid': '{name} outbid you as admirer of {target}',
  'notify.gift': '{name} sent you {emoji}',
  'notify.achievement': 'Achievement: {title} ★{level} (+❤ {reward})',
  'notify.referral': '+❤ {bonus}: {name} joined the game with your invite!',
  'notify.wallet': '+❤ {amount}',
  'notify.gm': '💰 +{amount} GM',
  'notify.transfer_gm': '{name} sent you {amount} GM',
  'notify.transfer_hearts': '{name} sent you ❤ {amount}',

  'admin.live_reported': '🔴 The live stream of {name} was ended after {reports} reports.',
  'admin.auto_mute': '⚠️ Auto-mute: player {userId} received {reports} reports in 24 hours.',
  'admin.broadcast_done': '📣 Broadcast finished: sent {sent}, failed {failed}.',

  'bot.welcome':
    'Hey! Get ready to dive into the ultimate Spin the Bottle adventure.\n\n👀 See who’s around you and start a conversation\n🎁 Exchange fun surprises and playful dares\n💌 Send hidden messages to someone special\n🎲 Enjoy entertaining games together in the room\n🎶 Play along with music in the background\n\n🎮 Tap PLAY and let the fun start!',
  'bot.play_button': '🎮 PLAY',
  'bot.menu_button': 'PLAY',
  'bot.help': 'Spin the Bottle is played in the Mini App: tap 🎮 PLAY.',
  'bot.rate_limited': 'Too many requests. Slow down a little.',
  'bot.banned': 'Your account is blocked.',
  'bot.gm_credited': '💰 +{amount} GM credited to your account. Balance: {gm} GM',
  'bot.gm_debited': '💰 {amount} GM was taken from your account by an admin. Balance: {gm} GM',
  'bot.short_description': 'Spin the bottle, kiss, send gifts and play music 💋',
  'bot.description':
    'The classic Spin the Bottle party game in Telegram: tables of 12, kisses, gifts, leagues, the Wheel of Fortune and music. 18+.',
  'bar.heart_shop': 'Heart shop',
  'bar.wheel': 'Wheel of Fortune',
  'bar.league': 'League',
  'bar.misc': 'Misc',
  'bar.settings': 'Settings',
  'bar.exit': 'Exit',
  'bar.table': 'Table',
  'bar.change_table': 'Change table',
  'menu.achievements': 'Achievements',
  'menu.ratings': 'Ratings',
  'menu.bottle': 'Bottle',
  'menu.styling': 'Styling',
  'menu.boosters': 'Boosters',
  'table.spin_the_bottle': 'Spin the bottle!',
  'table.spins_the_bottle': 'Spins the bottle',
  'table.order_song': 'Order a song for ❤ {price}',
  'table.order_video': 'Order a music video for ❤ {price}',
  'table.video_size': 'Video size',
  'table.play_here': 'Play here',
  'gifts.send_to': 'Send a gift to',
  'player.profile': 'Profile',
  'player.admire_for': 'Admire for',
  'player.reported': 'Thank you, we will check it.',
  'report.SPAM': 'Spam',
  'report.ABUSE': 'Insults',
  'report.SEXUAL_CONTENT': 'Sexual content',
  'report.UNDERAGE': 'Under 18',
  'report.OTHER': 'Other',
  'shop.title': 'Buy hearts',
  'shop.details': 'Details',
  'shop.vip_status': 'STATUS',
  'shop.for_friend_1': 'Get',
  'shop.for_friend_2': 'for a friend',
  'shop.bonus': '{percent}% BONUS',
  'shop.best_offer': 'Best offer',
  'shop.best_pick': 'Best pick',
  'shop.send': 'Send',
  'shop.need_gm': 'You need {gm} GM. Ask the admin to top up your GM balance.',
  'shop.bought': '+❤ {hearts}',
  'shop.send_gm_title': 'Send GM {amount} to a friend',
  'shop.send_hearts_title': 'Send ❤ {amount} to a friend',
  'shop.send_left': 'Sends left today: {count}',
  'shop.send_no_friends': 'Add friends first: tap a player at the table, then the green +.',
  'shop.sent_gm': '{amount} GM sent to {name}',
  'shop.sent_hearts': '❤ {amount} sent to {name}',
  'vip.perks':
    'Become VIP: send unique gifts, use emojis and stand out among the rest with a special name color!',
  'wheel.rules': 'Hit “Spin” — start the Wheel of Fortune and get your reward!',
  'wheel.chances_label': 'Chances:',
  'wheel.price': 'Price:',
  'wheel.winner': 'Winner',
  'wheel.jackpot_text': 'hit the jackpot and got ❤ 1000',
  'wheel.big_win_text': 'won {prize}',
  'league.hint': 'Use boosters to get to higher positions',
  'league.boosters': 'Boosters',
  'league.zone_up_hint': 'go to the upper league',
  'league.final_in': 'Final results in: {hours} hr.',
  'league.until_limit': 'Until the limit is reached:',
  'league.activate': 'Activate',
  'league.rules_1': 'Leagues are daily competitions where you can win hearts and unique gifts.',
  'league.rules_2':
    'Points are scored for gifts, kisses received, music played and admiration. One point for every heart spent: the more gifts you send, the more points you get.',
  'league.rules_3':
    'At the end of the day the first {promote} players move up to the next league and get chests; the last {demote} move down.',
  'league.rules_4':
    'Kisses received give at most {cap} points a day. Boosters from the Wheel of Fortune help: double points, a higher limit and bonus points.',
  'achievements.mine': 'My achievements',
  'achievements.count': '{stars}/{total}',
  'achievements.show_first': 'Show first:',
  'achievements.completed': 'completed',
  'achievements.locked': 'locked',
  'achievements.detail': 'Achievement',
  'achievements.meet': 'Meet the conditions to get:',
  'ratings.help_title': 'About this rating',
  'ratings.period': 'Period',
  'ratings.top_n': 'Top-{n} of best players',
  'ratings.period_all': 'all time',
  'ratings.period_month': 'this month',
  'ratings.period_week': 'this week',
  'ratings.period_day': 'today',
  'styling.apply': 'Apply',
  'styling.owned_hint': 'You own this frame.',
  'styling.buy_hint': 'Buy this frame for ❤ {price} and wear it.',
  'styling.locked': 'This frame is a reward.',
  'settings.sounds': 'Sounds',
  'settings.invite': 'Invite friends',
  'settings.friends': 'My friends',
  'settings.contact': 'Contact us',
  'friends.who_sees': 'Who can see my friendships?',
  'friends.visibility_everyone': 'Everyone',
  'friends.visibility_me': 'Only me',
  'friends.sent': 'Requests sent: {count}',
  'activity.hours': '{hours} h',
  'activity.more': 'MORE',
  'activity.less': 'LESS',
  'activity.h': 'h',
  'activity.m': 'm',
  'activity.pts': '{points} pts',
  'activity.my_place': 'Your place: #{rank} · {points} pts',
  'portal.logout': 'Logout',
  'portal.services': 'Services',
  'portal.invited': 'Invited users',
  'portal.invited_empty': 'No one has joined with your link yet.',
  'portal.no_status': 'No status yet',
  'portal.status_title': 'Your status',
  'portal.status_placeholder': 'Write something about yourself…',
  'portal.start': 'Start',
  'portal.watch': 'Watch',
  'portal.license': 'License',
  'portal.license_title': '{name} v2',
  'portal.license_text': '© {year}. All rights reserved. Kisses and gifts are virtual and just for fun.',
  'services.store': 'Store',
  'services.balance': 'Balance:',
  'services.username': 'Username',
  'services.game_name': 'Game Name',
  'services.profile': 'Profile',
  'services.password': 'Change Password',
  'services.password_text': 'Change your account password.',
  'services.gender': 'Change Gender',
  'services.gender_text': 'Change your gender information.',
  'services.name': 'Change Game Name',
  'services.name_text': 'Change the name visible in games.',
  'services.photo': 'Change Profile Picture',
  'services.photo_text': 'Customize your profile picture.',
  'services.back': 'BACK',
  'services.password_current': 'Current password',
  'services.password_new': 'New password (at least 6 characters)',
  'services.password_repeat': 'Repeat the new password',
  'services.password_hint': 'With a password you can log in on the website with your ID.',
  'services.password_mismatch': 'The passwords do not match.',
  'services.password_saved': 'Password saved.',
  'services.gender_later': 'You can change it again after {date}.',
  'services.name_telegram': 'Use my Telegram name ({name})',
  'services.photo_pick': 'Choose a photo',
  'services.photo_telegram': 'Use my Telegram photo',
  'login.title': 'Log in',
  'login.login': 'ID or @username',
  'login.password': 'Password',
  'login.submit': 'Log in',
  'login.hint': 'Set a password in the game: exit → Services → Change Password.',
  'login.open_bot': 'Open the game in Telegram',
  'login.developer': 'Developer login',
  'watch.back': 'Back',
  'watch.live_caps': 'LIVE',
  'watch.live_tab': 'Live ({count})',
  'watch.domino_tab': 'Domino ({count})',
  'watch.no_streams': 'No active broadcast found.',
  'watch.first_stream': 'Start the first broadcast',
  'live.no_other_camera': 'There is no other camera.',
  'courting.title': 'Courting',
  'courting.text': '{name} started admiring you for',
  'courting.ok': 'Alright',

  // Daily horoscope (menu ≡ → Horoscope).
  'menu.horoscope': 'Horoscope',
  'horoscope.title': 'Daily horoscope',
  'horoscope.pick': 'Choose your sign',
  'horoscope.today': 'Today, {date}',
  'horoscope.love_title': 'Love',
  'horoscope.lucky_number': 'Lucky number',
  'horoscope.lucky_color': 'Lucky colour',
  'horoscope.change': 'Change sign',
  'portal.credits': '3D pictures: Microsoft Fluent Emoji (MIT). Zodiac signs: Freepik.',
  'horoscope.sign.aries': 'Aries',
  'horoscope.sign.taurus': 'Taurus',
  'horoscope.sign.gemini': 'Gemini',
  'horoscope.sign.cancer': 'Cancer',
  'horoscope.sign.leo': 'Leo',
  'horoscope.sign.virgo': 'Virgo',
  'horoscope.sign.libra': 'Libra',
  'horoscope.sign.scorpio': 'Scorpio',
  'horoscope.sign.sagittarius': 'Sagittarius',
  'horoscope.sign.capricorn': 'Capricorn',
  'horoscope.sign.aquarius': 'Aquarius',
  'horoscope.sign.pisces': 'Pisces',
  'horoscope.general.0': 'A good day to meet new people. Say hello first and see where it leads.',
  'horoscope.general.1': 'Your energy is high today. Spin the bottle more than once!',
  'horoscope.general.2': 'Someone at the table is thinking about you more than you know.',
  'horoscope.general.3': 'Keep things light today: a joke will open more doors than a serious talk.',
  'horoscope.general.4': 'A small gift can make a big impression today.',
  'horoscope.general.5': 'Trust your first impression. It will not let you down today.',
  'horoscope.general.6': 'A calm day. Enjoy the music and the company.',
  'horoscope.general.7': 'Today luck is on the side of the brave. Take the first step.',
  'horoscope.general.8': 'An old friend may surprise you today. Keep your messages open.',
  'horoscope.general.9': 'Patience pays off today. What you want will come by evening.',
  'horoscope.love.0': 'A kiss today could be the start of something special.',
  'horoscope.love.1': 'Your smile is your best gift today.',
  'horoscope.love.2': 'Someone admires you quietly. Look around the table.',
  'horoscope.love.3': 'Be honest about your feelings. It will be appreciated.',
  'horoscope.love.4': 'A mutual kiss is very likely today.',
  'horoscope.love.5': 'Take it slow: the best things come with time.',
  'horoscope.color.0': 'Red',
  'horoscope.color.1': 'Pink',
  'horoscope.color.2': 'Gold',
  'horoscope.color.3': 'Blue',
  'horoscope.color.4': 'Green',
  'horoscope.color.5': 'Purple',
};
